import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { SubTitleStyle, TitleStyle } from '../../styles/styles';
import { BREAKPOINTS, COLORS_BASE } from '../../utils/consts';
import { useLocation, useNavigate } from 'react-router-dom';
import DownloadTable from './DownloadTable';
import Footer from '../Shared/Footer';
import SectionContainer from '../Shared/SectionContainer';
import LinkButton from '../Shared/LinkButton';
import { faInfoCircle, faMap } from '@fortawesome/free-solid-svg-icons';
import SettlementProfilesCarousel from './SettlementProfilesCarousel';

function LandingPage() {
    let navigate = useNavigate();
    const exploreMapRef = useRef<HTMLDivElement>(null);
    const downloadTableRef = useRef<HTMLDivElement>(null);
    const location = useLocation();

    const openFeaturedSettlementOnMap = (id: number) => {
        navigate(`/map?featured_settlement=${id}`);
    };

    useEffect(() => {
        if (location.state?.scrollToDownloadTable && downloadTableRef.current) {
            downloadTableRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [location]);

    return (
        <>
        <LandingPageStyle>
            <HeroStyle>
                <SectionContainer backgroundColor='none' width='min(600px, 80%)' color='white' margin='0'>
                    <TitleStyle>
                        Lesotho's portal for data-driven electrification planning
                    </TitleStyle>
                    <SubTitleStyle>
                        The Lesotho electrification portal from the Department of Energy uses the
                        latest data and tools to support electrification planning through updated
                        analysis and ground truth surveys.
                    </SubTitleStyle>
                    <LinkButton to={'/about'} text={'Learn More'} icon={faInfoCircle} />
                </SectionContainer>
            </HeroStyle>
            <SectionContainer className="fade-in" backgroundColor={COLORS_BASE.GREYS.MAIN} flex padding='0rem' margin='0' justifyContent='center'>
                <LandingPageContentStyle flexDirection='column' ref={exploreMapRef}>
                    <TitleStyle>
                        Analyse and discover electrification data
                    </TitleStyle>
                    <SubTitleStyle>
                        Our interactive map combines planning layers with village-level survey signals.
                        Today it features three sample settlements, with a larger DOE survey release
                        of 50+ villages coming next.
                    </SubTitleStyle>
                </LandingPageContentStyle>
                <MapImageContainer>
                        <img src='/img/map_showcase.png' alt='map' style={{ maxWidth: '100%', height: 'auto' }} />
                        <ButtonOverlay>
                            <LinkButton to={'/map'} text={'Explore Map'} icon={faMap}/>
                        </ButtonOverlay>
                    </MapImageContainer>
            </SectionContainer>
            <SettlementProfilesCarousel onViewOnMap={openFeaturedSettlementOnMap} />
            <div id='datadownload'>	
                <SectionContainer backgroundColor={COLORS_BASE.GREYS.MAIN} flex padding='0rem' margin='0rem' justifyContent='center'>
                    <LandingPageContentStyle flexDirection='column'>
                        <TitleStyle>
                            Data Download
                        </TitleStyle>
                        <SubTitleStyle>
                            Download a range of datasets related to minigrids and electrification planning.
                        </SubTitleStyle>
                        <DownloadTable />
                    </LandingPageContentStyle>
                </SectionContainer>
            </div>
            <Footer/>
        </LandingPageStyle>
        </>
    )
}

const LandingPageStyle = styled.div<{}>(() => `	
`)

export const LandingPageContentStyle = styled.div<{ flexDirection?: string, alignItems?:string, textAlign?: string, width?: string, flexWrap?: string, marginTop?: string, justifyContent?: string }>(({ justifyContent='flex-start', flexDirection = 'row', textAlign = 'center', flexWrap = 'nowrap', alignItems = 'stretch', width = 'inherit' }) => `
    display: flex;
    flex-direction: ${flexDirection};
    flex-wrap: ${flexWrap};
    align-items: ${alignItems};
    align-content: ${alignItems};
    justify-content: ${justifyContent};
    width: ${width};
    padding: 0;
    text-align: ${textAlign};

        @media (max-width: ${BREAKPOINTS.MOBILE}) {
            width: 100%;
        }
`)

const HeroStyle = styled.header<{}>(() => `
    position: relative;
    justify-content: center;
    align-items: center;
    height: max(800px, 60vh);
    background-image: url('/img/hero-img.jpg');
    background-size: cover;
    background-position: center;
    // overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.6); /* Adjust the opacity as needed */
        z-index: 1;
        // opacity: 0;
        // animation: fadeIn 2s forwards;
    }

    > * {
        position: relative;
        z-index: 2;
        // opacity: 0;
        // animation: fadeIn 2s forwards 1s; /* Delay to sync with overlay */
    }

    // @keyframes fadeIn {
    //     to {
    //         opacity: 1;
    //     }
    // }

    @media (max-width: ${BREAKPOINTS.MOBILE}) {
        height: min(720px, 84vh);
        background-position: 60% center;
    }
`)

const MapImageContainer = styled.div<{}>(() => `
    position: relative;

    img {
      display: block;
      width: 100%;
      height: auto;
      max-width: 100%;
    }
`)

const SurveyImageContainer = styled.div<{}>(() => `
    position: relative;
    width: 50%;
    height: auto;
`)

const SurveyImgStyle = styled.img<{}>(() => `
    max-width: 100%;
`)  

const ButtonOverlay = styled.div<{ left?: string }>(({ left = '50%' }) => `
    position: absolute;
    top: 50%;
    left: ${left};
    transform: translate(-50%, -50%);

        @media (max-width: ${BREAKPOINTS.MOBILE}) {
            width: calc(100% - 24px);
            display: flex;
            justify-content: center;
        }
`)

const SurveyImgButtonOverlay = styled.div<{ left?: string }>(({ left = '50%' }) => `
    position: absolute;
    top: 50%;
    left: ${left};
    transform: translate(-50%, -50%);    
`)

export default LandingPage;